import { useSearch } from '@tanstack/react-router';
import { useMemo } from 'react';

import type { Profile } from '../../../../entities/post/models';
import { DataTable, type TableColumnConfig } from '../../../../shared/components/data-table';
import { ProfileCell } from '../../people/components/profile-cell/profile-cell';
import { getProfileUrl } from '../../people/helpers/get-profile-url';
import { useOverviewProfiles } from '../../shared/hooks/use-insight-overview';
import { showProfilePopup } from '../../shared/profile-popup/index';

import { useSharedDateRange } from '@/shared/hooks/use-shared-date-range';
import { usePublicFilter } from '../../shared/public-filter/use-public-filter';

interface Props {
  onProfileClick?: (profile: Profile, event: React.MouseEvent) => void;
  onNavigate?: () => void;
  title?: string;
  pageSize: number;
  minHeight?: string;
}

export function SimpleProfileTable({
  onProfileClick,
  onNavigate,
  title = 'Top profiles',
  pageSize,
}: Props) {
  const { q } = useSearch({ strict: false }) as { q: string };
  const { dateRange } = useSharedDateRange();
  const { publicFilter } = usePublicFilter(q);
  const { data: profiles, isLoading } = useOverviewProfiles({
    queryId: q,
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    limit: 5,
    publicFilter,
  });
  const columns: TableColumnConfig<Profile>[] = useMemo(
    () => [
      {
        key: 'username',
        header: 'Profiles',
        size: '1fr',
        tableCellClassName: 'p-0',
        cell: ({ row }) => (
          <ProfileCell
            profileImageUrl={row.original.profileImageUrl}
            username={row.original.username}
            followerCount={row.original.followerCount}
            className="cursor-pointer p-4"
            platform={row.original.platform}
            onClick={async (event) => {
              event.stopPropagation();
              await showProfilePopup({
                position: { x: event.clientX, y: event.clientY },
                profileImageUrl: row.original.profileImageUrl,
                username: row.original.username,
                followerCount: row.original.followerCount,
                profileUrl: getProfileUrl(row.original.username, row.original.platform),
              });
            }}
          />
        ),
      },
      {
        key: 'postCount',
        header: 'Volume',
        size: '100px',
        cell: ({ getValue }) => <DataTable.Cell getValue={getValue} />,
      },
    ],
    [],
  );

  const handleRowClick = (profile: Profile, event: React.MouseEvent) => {
    onProfileClick?.(profile, event);
  };

  return (
    <DataTable
      data={profiles?.profiles || []}
      isLoading={isLoading}
      header={<DataTable.Header title={title} onRightClick={onNavigate} />}
      columns={columns}
      onRowClick={handleRowClick}
      pageSize={pageSize}
      enablePagination={false}
    />
  );
}
